/**
 * The fan's identity for event attribution, read off `window.urcAuth`.
 *
 * WHY THIS IS A SECOND COPY, AND NOT AN IMPORT OF THE LOYALTY ONE
 * --------------------------------------------------------------
 * `resources/scripts/loyalty/urcAuth.js` does the same job for the loyalty UI. This module belongs
 * to the `app` Vite entry and that one to `loyalty`; importing across that line would make Rollup
 * hoist a shared chunk and rewrite the module graph of the shipped loyalty bundle — the bundle that
 * carries the fan's credential. That is [D-055](../../../../docs/decisions.md), and the price of
 * obeying it is this file.
 *
 * **It is a much smaller price than the one D-055 used to charge.** What lived here was a JWT
 * parser, an audience check, an expiry margin, a duplicate-cookie-safe reader, a capture taken at
 * construction and a logout tombstone — a near-copy of `session.js`, kept in step by hand, and the
 * two had to be changed together. All of it existed because the token was in a cookie a third-party
 * script deleted on every page load. What is left is one accessor over a global.
 *
 * WHAT THE SDK'S CONTRACT REQUIRES, AND IT IS EASY TO GET WRONG
 * ------------------------------------------------------------
 * `@3halves-labs/score-tracker` calls the token provider at `init()` and again **before every
 * flush**, and its `refreshToken()` assigns `this.jwt = v == null ? undefined : v` — so `null` and
 * `undefined` both mean "attribute this batch anonymously", and an **empty string does not**. An
 * `''` would be stamped into the envelope's `context.jwt` as though it were a JWT. Hence the
 * mapping in `createTokenProvider()`, which is the one line in this file with a consequence.
 *
 * The token identifies the fan to 3Halves. It is never logged and never stored — it is asked for
 * when the SDK asks, and travels in the ingest envelope's body.
 */

/**
 * Build a reader over an injected `window`-like accessor.
 *
 * @param {() => (Object|undefined)} getContract Returns the `urcAuth` object, or undefined.
 * @return {{getToken: () => Promise<string>}} The reader.
 */
export function createAuthReader( getContract ) {
	/**
	 * A bearer for the loyalty audience, or ''.
	 *
	 * Every failure is '' rather than a rejection: the SDK calls this before a flush, and a
	 * rejection there would surface as a failed batch rather than as an anonymous one.
	 *
	 * **A settled signed-out visitor is answered without asking the SDK**, for the reason
	 * `resources/scripts/loyalty/urcAuth.js` gives at more length: this is called before every
	 * flush, which is every 2.5s while events are queued, and on the hosted installs each cache
	 * miss is a hidden `/authorize` iframe. `null` — not answered yet — is not short-circuited.
	 *
	 * @return {Promise<string>} The token, or ''.
	 */
	function getToken() {
		const api = getContract();

		if ( ! api || typeof api.getAccessToken !== 'function' ) {
			return Promise.resolve( '' );
		}

		const state = typeof api.state === 'function' ? api.state() : null;

		if ( state && ! state.signedIn ) {
			return Promise.resolve( '' );
		}

		return Promise.resolve( api.getAccessToken() )
			.then( token => ( typeof token === 'string' ? token : '' ) )
			.catch( () => '' );
	}

	return { getToken };
}

/**
 * The signed-in fan's `sub`, or '' where there is not one to have.
 *
 * **Why this is not an import of `fanKey()` from `resources/scripts/auth/state.js`**, which is the
 * same three lines: that module belongs to the `auth` entry and this one to `app`, and D-055 is
 * about exactly that edge -- the copy is the price, as the header explains. What is copied is the
 * *rule*, and the rule is the part worth stating: an absent, empty or `'unknown'` `sub` is "we
 * cannot tell yet", never an identity. `auth0-login.php` wrote the literal `'unknown'` into
 * `auth0_user_id` for more than one fan, so treating it as an id would merge them.
 *
 * `null` from `state()` means the SDK has not answered yet, which on a cold page is the first few
 * hundred milliseconds -- it is not "signed out", and it answers '' here for the same reason.
 *
 * @param {Object|null} state An AuthState, or null.
 * @return {string} The `sub`, or ''.
 */
export function fanId( state ) {
	if ( ! state || ! state.signedIn || typeof state.sub !== 'string' ) {
		return '';
	}

	const sub = state.sub.trim();

	return sub === 'unknown' ? '' : sub;
}

/**
 * Subscribe to every settled state of the fan, current one first. A no-op where the contract is
 * absent.
 *
 * **`onIdentityChange` is not enough on its own, and this is the whole reason this exists.** It
 * fires on a CHANGE of fan and replays only for a late subscriber where a change was already
 * detected in this document. A fan who arrives already signed in -- the ordinary case on a
 * multi-page site where every navigation is a full load -- never triggers it, so an `identify()`
 * driven from it alone would never fire for the fans it is for.
 *
 * @param {() => (Object|undefined)} getContract Returns the `urcAuth` object, or undefined.
 * @param {(state: Object) => void}  listener    Called with each AuthState.
 * @return {() => void} Unsubscribe.
 */
export function onFan( getContract, listener ) {
	const api = getContract();

	if ( ! api || typeof api.subscribe !== 'function' ) {
		return () => {};
	}

	return api.subscribe( listener );
}

/**
 * Subscribe to a change of fan. A no-op where the contract is absent.
 *
 * @param {() => (Object|undefined)} getContract Returns the `urcAuth` object, or undefined.
 * @param {(state: Object) => void}  listener    Called with the new AuthState.
 * @return {() => void} Unsubscribe.
 */
export function onIdentityChange( getContract, listener ) {
	const api = getContract();

	if ( ! api || typeof api.onIdentityChange !== 'function' ) {
		return () => {};
	}

	return api.onIdentityChange( listener );
}

/**
 * The live contract, or undefined. Re-read on every call rather than captured once.
 *
 * The auth bundle is a deferred module and this one may be evaluated in the same tick, so a value
 * captured at import could be `undefined` for the life of the page. `App\Auth\Assets` enqueues at
 * priority 9 to make the ordering hold, but a reader that depends on an enqueue priority breaks
 * when somebody changes one.
 *
 * @return {Object|undefined} The contract.
 */
export function liveContract() {
	return typeof window === 'undefined' ? undefined : window.urcAuth;
}

/**
 * Build the token provider handed to the SDK.
 *
 * There is no cheap "is this fan signed in" pre-check in front of it. One existed to spare an
 * anonymous visitor a round trip; with no round trip to spare it could only do harm, by reporting
 * a fan with a live token as anonymous whenever an unsigned cookie disagreed with the credential.
 * The short-circuit inside `getToken()` is the version of that idea which is safe, because it reads
 * the same source the token comes from.
 *
 * @param {Object} options        Collaborators.
 * @param {Object} options.reader From `createAuthReader()`.
 * @return {() => Promise<string|undefined>} The provider. Resolves undefined for anonymous.
 */
export function createTokenProvider( { reader } ) {
	return () => reader.getToken().then( token => token || undefined );
}
